// История операций: страницы выписки по дням, у каждого дня — свой итог.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { Transaction, TransactionFilters } from '../api/types';
import { CategoryMark } from '../components/CategoryMark';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { FiltersSheet } from '../components/FiltersSheet';
import { TransactionModal } from '../components/TransactionModal';
import { FilterIcon, PencilIcon, TrashIcon } from '../components/icons';
import { humanError } from '../lib/errors';
import { haptic } from '../lib/telegram';
import { useToast } from '../lib/toast';
import { useViewCurrency } from '../lib/view-currency';

type RowMenuMode = null | { tx: Transaction };

type Props = {
  // Префиксованные фильтры — приходят один раз при заходе с другого экрана (drill-down).
  initialFilters?: TransactionFilters | null;
  onConsumeInitialFilters?: () => void;
};

export function Transactions({ initialFilters, onConsumeInitialFilters }: Props = {}): JSX.Element {
  const [filters, setFilters] = useState<TransactionFilters>(() =>
    initialFilters ? { limit: 100, ...initialFilters } : { limit: 100 },
  );
  const [showFilters, setShowFilters] = useState(false);
  const [rowMenu, setRowMenu] = useState<RowMenuMode>(null);
  const [confirmDelete, setConfirmDelete] = useState<Transaction | null>(null);
  const [editTx, setEditTx] = useState<Transaction | null>(null);

  // Если родитель пришёл с новыми initialFilters (drill-down при уже открытом экране) — применяем.
  useEffect(() => {
    if (initialFilters) {
      setFilters({ limit: 100, ...initialFilters });
      onConsumeInitialFilters?.();
    }
  }, [initialFilters, onConsumeInitialFilters]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['transactions', filters],
    queryFn: () => api.transactions(filters),
  });

  const queryClient = useQueryClient();
  const toast = useToast();
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteTransaction(id),
    onSuccess: () => {
      haptic('success');
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['kpi'] });
      setConfirmDelete(null);
      setRowMenu(null);
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось удалить операцию')),
  });

  const hasActiveFilters =
    filters.period !== undefined || filters.kind !== undefined || !!filters.category || !!filters.q;

  if (isLoading) return <div className="loading">Загружаем…</div>;
  if (error) return <div className="error">{humanError(error, 'История не загрузилась')}</div>;
  if (!data) return <div className="error">Нет данных</div>;

  return (
    <div className="screen">
      <div className="list-toolbar">
        <h2 className="screen-title">Операции</h2>
        <button
          type="button"
          className={`filters-btn ${hasActiveFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(true)}
        >
          <FilterIcon size={15} />
          Фильтры
          {hasActiveFilters ? <span className="filters-dot" /> : null}
        </button>
      </div>

      {data.length === 0 ? (
        <div className="empty">
          {hasActiveFilters
            ? 'По фильтрам ничего не найдено.'
            : 'Пока пусто. Напишите боту «Кофе 30000» — появится первая запись.'}
        </div>
      ) : (
        renderGroupedList(data, (tx) => setRowMenu({ tx }))
      )}

      {showFilters ? (
        <FiltersSheet
          initial={filters}
          onApply={(f) => setFilters({ ...f, limit: filters.limit ?? 100 })}
          onClose={() => setShowFilters(false)}
        />
      ) : null}

      {rowMenu ? (
        <div className="dialog-backdrop" onClick={() => setRowMenu(null)} role="presentation">
          <div
            className="dialog"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="dialog-title">
              {rowMenu.tx.note || rowMenu.tx.category_name || 'Операция'}
            </div>
            <div className="dialog-actions stack">
              <button
                type="button"
                className="btn-secondary btn-with-icon"
                onClick={() => {
                  setEditTx(rowMenu.tx);
                  setRowMenu(null);
                }}
              >
                <PencilIcon size={16} />
                Изменить
              </button>
              <button
                type="button"
                className="btn-secondary btn-with-icon danger-text"
                onClick={() => {
                  setConfirmDelete(rowMenu.tx);
                  setRowMenu(null);
                }}
              >
                <TrashIcon size={16} />
                Удалить
              </button>
              <button type="button" className="btn-secondary" onClick={() => setRowMenu(null)}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmDelete ? (
        <ConfirmDialog
          title="Удалить операцию?"
          description="Запись исчезнет из истории и из расчётов за месяц. Отменить нельзя."
          confirmLabel="Удалить"
          destructive
          loading={deleteMut.isPending}
          onConfirm={() => deleteMut.mutate(confirmDelete.id)}
          onCancel={() => setConfirmDelete(null)}
        />
      ) : null}

      {editTx ? (
        <TransactionModal mode="edit" initial={editTx} onClose={() => setEditTx(null)} />
      ) : null}
    </div>
  );
}

function renderGroupedList(
  data: Transaction[],
  onRowClick: (tx: Transaction) => void,
): JSX.Element {
  const groups = new Map<string, Transaction[]>();
  for (const t of data) {
    const day = t.occurred_at.slice(0, 10);
    const arr = groups.get(day) ?? [];
    arr.push(t);
    groups.set(day, arr);
  }
  const days = Array.from(groups.keys()).sort().reverse();
  return (
    <>
      {days.map((day) => (
        <DayGroup key={day} day={day} items={groups.get(day) ?? []} onRowClick={onRowClick} />
      ))}
    </>
  );
}

function DayGroup({
  day,
  items,
  onRowClick,
}: {
  day: string;
  items: Transaction[];
  onRowClick: (tx: Transaction) => void;
}): JSX.Element {
  const { formatShortAmount } = useViewCurrency();
  const net = dayNetMinor(items);
  return (
    <div className="tx-day">
      <div className="tx-day-head">
        <div className="tx-day-label">{formatDay(day)}</div>
        {net !== null ? (
          <div className="tx-day-total">
            {net >= 0n ? '+' : '−'}
            {formatShortAmount((net >= 0n ? net : -net).toString())}
          </div>
        ) : null}
      </div>
      {items.map((t) => (
        <TransactionRow key={t.id} tx={t} onClick={() => onRowClick(t)} />
      ))}
    </div>
  );
}

// Итог дня считаем только когда все операции в одной валюте (UZS) — иначе
// складывать нечего, и честнее не показать ничего, чем показать неверное.
function dayNetMinor(items: Transaction[]): bigint | null {
  if (items.some((t) => t.currency.toUpperCase() !== 'UZS')) return null;
  let net = 0n;
  for (const t of items) {
    const v = BigInt(t.amount_minor);
    net += t.kind === 'expense' ? -v : v;
  }
  return net;
}

function formatDay(yyyyMmDd: string): string {
  const [y, m, d] = yyyyMmDd.split('-');
  if (!y || !m || !d) return yyyyMmDd;
  const months = [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря',
  ];
  return `${Number(d)} ${months[Number(m) - 1] ?? m} ${y}`;
}

function TransactionRow({
  tx,
  onClick,
}: {
  tx: Transaction;
  onClick: () => void;
}): JSX.Element {
  // Это чистый столбец цифр — здесь повторённая единица вредит больше всего:
  // на экране её было шестнадцать штук, и глаз вёл по «м», а не по разрядам.
  // Записи в чужой валюте обязаны называть себя, они в меньшинстве.
  const { formatAmount, formatMoney } = useViewCurrency();
  const sign = tx.kind === 'expense' ? '−' : '+';
  const label = tx.category_name ?? tx.category_slug ?? null;
  return (
    <button type="button" className={`tx-row ${tx.kind}`} onClick={onClick}>
      <CategoryMark name={label ?? tx.note ?? '?'} slug={tx.category_slug} size="sm" />
      <div className="tx-main">
        <div className="tx-title">{tx.note || label || 'Без категории'}</div>
        {tx.note && label ? <div className="tx-note">{label}</div> : null}
      </div>
      <div className="tx-amount">
        {sign}
        {tx.currency === 'UZS'
          ? formatAmount(tx.amount_minor, tx.currency)
          : formatMoney(tx.amount_minor, tx.currency)}
      </div>
    </button>
  );
}
